import { useModal } from '@billy/design-system'
import { useMemo } from 'react'
import { useQuery } from 'react-query'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { defaultStaleTime } from '../../../config/queryClient'
import { ModalId } from '../../../enums/modalId'
import { QueryKeys } from '../../../enums/queryKeys'
import { Scope } from '../../../enums/scope'
import { useQueryParams } from '../../../hooks/useQueryParams'
import { isAuthorized } from '../../../utils/isAuthorized'
import { fetchOpenBankingAccounts } from '../query-api'
import { OpenBankingAccount } from '../types/openBankingAccount'

interface UseOpenBankingAccountsProps {
  enabled?: boolean
  withDeleted?: boolean
}

export const useOpenBankingAccounts = (props?: UseOpenBankingAccountsProps) => {
  const { enabled = true, withDeleted } = props || {}

  const [queryParams] = useQueryParams({
    defaultQuery: { success: undefined, connectionId: undefined },
    withDefaultQueryInUrl: false,
  })
  const { organization } = useUserOrganization()
  const { open: openErrorModal } = useModal(ModalId.BankConnectionErrorModal)

  const canUseBankIntegration = isAuthorized(Scope.BankSyncRead)

  const {
    data: openBankingAccounts,
    isError,
    isLoading,
    refetch,
  } = useQuery(
    [QueryKeys.OpenBankingAccounts, withDeleted],
    () => fetchOpenBankingAccounts(organization?.id as string, { withDeleted }),
    {
      enabled: !!organization?.id && canUseBankIntegration && enabled,
      onError: openErrorModal,
      staleTime: defaultStaleTime,
    },
  )

  const filteredOpenBankingAccounts: OpenBankingAccount[] | undefined = useMemo(() => {
    if (!queryParams.connectionId || !openBankingAccounts) {
      return undefined
    }
    return openBankingAccounts.filter(
      (bankConnection) =>
        !bankConnection.externalAccountId && bankConnection.connection.id === queryParams.connectionId,
    )
  }, [openBankingAccounts, queryParams.connectionId])

  return {
    openBankingAccounts,
    filteredOpenBankingAccounts,
    isError,
    isLoading,
    refetch,
  }
}
